import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import {
  trackArtistManagerActivity,
  ARTIST_MANAGER_ACTIVITY_TYPES,
} from "../api/artistManager/progress.api";
import { getCreditDetails } from "../redux/actions/billing";
import { notifyRewardCompletions } from "../redux/actions/backgroundProcesses";

export const useArtistManagerActivityTracker = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const invalidateRewards = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["rewards"] });
  }, [queryClient]);

  const applyMilestonesToRewardsCache = useCallback(
    (milestones) => {
      queryClient.setQueryData(["rewards"], (oldData) => {
        if (!oldData) return oldData;
        const modules = Array.isArray(oldData) ? oldData : oldData.data;
        if (!Array.isArray(modules)) return oldData;

        const updatedModules = modules.map((module) => {
          let newlyCompleted = 0;
          const updatedRewards = (module.rewards || []).map((reward) => {
            if (reward.isCompleted) return reward;

            const milestone = milestones.find((m) => {
              const normalize = (s) =>
                String(s || "").toLowerCase().replace(/[\s_-]/g, "");

              const milestoneActivityType =
                m.activityType || m.unlockCondition?.activityType || m;
              const milestoneTitle = m.title || m.activityType || "";

              if (
                milestoneActivityType &&
                normalize(reward.unlockCondition?.activityType) ===
                  normalize(milestoneActivityType)
              ) {
                return true;
              }
              if (
                milestoneTitle &&
                reward.title &&
                normalize(reward.title).includes(normalize(milestoneTitle))
              ) {
                return true;
              }
              return false;
            });

            if (milestone) {
              newlyCompleted += 1;
              return { ...reward, isCompleted: true, isUnlocked: true };
            }
            return reward;
          });

          return newlyCompleted > 0
            ? {
                ...module,
                rewards: updatedRewards,
                completedRewards: (module.completedRewards || 0) + newlyCompleted,
              }
            : module;
        });

        return Array.isArray(oldData)
          ? updatedModules
          : { ...oldData, data: updatedModules };
      });
    },
    [queryClient]
  );

  // These activity types ARE the final reward milestone (single-fire, no
  // threshold aggregation needed) — so we can optimistically mark the
  // matching reward as completed in the UI the instant the event fires,
  // without waiting on the backend to recognize the activityType.
  const SINGLE_FIRE_MILESTONES = [
    ARTIST_MANAGER_ACTIVITY_TYPES.CREATE_FIRST_BIO_LINK,
    ARTIST_MANAGER_ACTIVITY_TYPES.CREATE_FIRST_PRESAVE_LINK,
    ARTIST_MANAGER_ACTIVITY_TYPES.SETUP_FIRST_FUTURE_SAVE,
  ];

  const getLocalMilestones = useCallback((activityType) => {
    if (SINGLE_FIRE_MILESTONES.includes(activityType)) {
      return [{ activityType, title: activityType }];
    }
    return [];
  }, []);

  const track = useCallback(
    async (activityType, metadata = {}) => {
      const localMilestones = getLocalMilestones(activityType);
      if (localMilestones.length > 0) {
        applyMilestonesToRewardsCache(localMilestones);
      }

      try {
        const result = await trackArtistManagerActivity(activityType, metadata);
        const backendMilestones = result?.milestonesReached || [];

        if (backendMilestones.length > 0) {
          applyMilestonesToRewardsCache(backendMilestones);
          dispatch(notifyRewardCompletions(backendMilestones));
          // A reward just completed server-side, which may have credited AI
          // credits — refresh the sidebar balance immediately.
          dispatch(getCreditDetails());
        }
        invalidateRewards();

        return result;
      } catch (error) {
        // Fail silently — activity tracking should not block the user experience.
        console.error("[ArtistManager activity] tracking failed:", activityType, metadata, error);
        return { isFirstVisit: false, visitCount: 0, milestonesReached: [] };
      }
    },
    [applyMilestonesToRewardsCache, getLocalMilestones, invalidateRewards, dispatch]
  );

  const trackBioLinkCreated = useCallback(
    (metadata = {}) => track(ARTIST_MANAGER_ACTIVITY_TYPES.CREATE_FIRST_BIO_LINK, metadata),
    [track]
  );

  const trackPreSaveLinkCreated = useCallback(
    (metadata = {}) => track(ARTIST_MANAGER_ACTIVITY_TYPES.CREATE_FIRST_PRESAVE_LINK, metadata),
    [track]
  );

  const trackMusicLinkCreated = useCallback(
    (metadata = {}) => track(ARTIST_MANAGER_ACTIVITY_TYPES.CREATE_TEN_MUSIC_LINKS, metadata),
    [track]
  );

  const trackFutureSaveSetup = useCallback(
    (metadata = {}) => track(ARTIST_MANAGER_ACTIVITY_TYPES.SETUP_FIRST_FUTURE_SAVE, metadata),
    [track]
  );

  const trackTaskCompleted = useCallback(
    (metadata = {}) => track(ARTIST_MANAGER_ACTIVITY_TYPES.CREATE_AND_COMPLETE_TEN_TASKS, metadata),
    [track]
  );

  const trackExpenseLogged = useCallback(
    (metadata = {}) => {
      // A single logged expense counts towards both the 10-expense and
      // 30-expense milestones; the backend decides which one(s) complete.
      return Promise.all([
        track(ARTIST_MANAGER_ACTIVITY_TYPES.LOG_TEN_EXPENSES, metadata),
        track(ARTIST_MANAGER_ACTIVITY_TYPES.LOG_THIRTY_EXPENSES, metadata),
      ]);
    },
    [track]
  );

  const trackBookSession = useCallback(
    (metadata = {}) => track(ARTIST_MANAGER_ACTIVITY_TYPES.BOOK_YOUR_SESSION, metadata),
    [track]
  );

  return {
    track,
    trackBioLinkCreated,
    trackPreSaveLinkCreated,
    trackMusicLinkCreated,
    trackFutureSaveSetup,
    trackTaskCompleted,
    trackExpenseLogged,
    trackBookSession,
    ARTIST_MANAGER_ACTIVITY_TYPES,
  };
};

export { ARTIST_MANAGER_ACTIVITY_TYPES };
